/**
 * The edgy side of making money (players asked for it: not only safe jobs). Two ways to win big or lose it all:
 *
 * - YAHOO: the gadgets from Jumbo (the store app), a skill you build at your laptop, and then the jobs, small to big.
 *   Every job is a roll: you cash out (the credit alert), it comes to nothing, or they come for you: cars outside,
 *   the cell, bail or a friend. The heat builds with every job and cools off over days; a bust takes the gear and
 *   freezes most of your money. Nothing here teaches anything: it's a dice roll with consequences, like the street.
 * - TRADING (trading.ts): markets that move every few minutes, leverage, and liquidation.
 */
import { advance, earn, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localPrice, nudge } from './character.ts';
import { takeIntoCustody } from './custody.ts';
import { Rng } from './rng.ts';
import { homeCountry } from './travel.ts';
import { deliveryAddress, onItsWay, placeOrder } from './orders.ts';

export interface Gadget { id: string; label: string; price: number; note: string; /** how much it helps a job land (0..) */ edge: number; /** practice goes faster */ comfy?: boolean }
/** Jumbo: delivered to your door. */
export const GADGETS: Gadget[] = [
  { id: 'laptop', label: 'Laptop', price: 650_000, note: 'The one thing you can’t do without', edge: 0.08 },
  { id: 'phone2', label: 'A second phone and SIMs', price: 120_000, note: 'Nothing traced to your own number', edge: 0.05 },
  { id: 'headset', label: 'Headset with a mic', price: 45_000, note: 'For the calls', edge: 0.04 },
  { id: 'router', label: 'Router and a year of data', price: 90_000, note: 'No dropped connection mid-job', edge: 0.04 },
  { id: 'ringlight', label: 'Ring light', price: 35_000, note: 'For the video calls', edge: 0.02 },
  { id: 'chair', label: 'Ergonomic chair', price: 180_000, note: 'Long nights, no back pain', edge: 0.01, comfy: true },
  { id: 'table', label: 'Work table', price: 120_000, note: 'A proper desk for the setup', edge: 0.01, comfy: true },
  { id: 'chair-gaming', label: 'Racing gaming chair', price: 420_000, note: 'The bucket seat, the winged back, a pillow for your neck. Pick its colour', edge: 0.015, comfy: true },
  { id: 'desk-motor', label: 'Electric standing desk', price: 650_000, note: 'A motor in each leg: sit, or stand, at a button', edge: 0.015, comfy: true },
  { id: 'vr', label: 'VR headset', price: 380_000, note: 'Put it on and you’re in UNFOLD Strike, anywhere; and its pose for your photos', edge: 0 },
  { id: 'rig', label: 'Desktop and two monitors', price: 1_400_000, note: 'The big boys’ setup', edge: 0.08 },
];
export const gadgetPrice = (s: LifeState, g: Gadget) => localPrice(s, g.price, 1_000);

export type JobSize = 'small' | 'medium' | 'big';
export const JOBS: Record<JobSize, { label: string; blurb: string; lo: number; hi: number; win: number; bust: number; heat: number }> = {
  small: { label: 'A small job', blurb: 'Quick, a few hundred thousand, low risk', lo: 300_000, hi: 1_200_000, win: 1, bust: 0.02, heat: 6 },
  medium: { label: 'A bigger job', blurb: 'Millions, if it lands. More eyes on you', lo: 2_000_000, hi: 9_000_000, win: 0.72, bust: 0.07, heat: 13 },
  big: { label: 'The big one', blurb: 'Tens of millions. If it goes wrong, it goes very wrong', lo: 20_000_000, hi: 90_000_000, win: 0.45, bust: 0.14, heat: 24 },
};

export interface HustleState { skill: number; heat: number; heatAt: number; runs: number; wins: number; busts: number; gadgets: string[]; cashed: number; /** the colour each chair came in (gadget id → colour id) */ colours?: Record<string, string> }

/** the chairs' colours: the main one (a gaming chair's coloured panels; the ergonomic chair's whole body) */
export interface GadgetColour { id: string; label: string; hex: number }
export const GADGET_COLOURS: Record<string, GadgetColour[]> = {
  'chair-gaming': [{ id: 'red', label: 'Red', hex: 0xb3121d }, { id: 'blue', label: 'Blue', hex: 0x1e4fd8 }, { id: 'pink', label: 'Pink', hex: 0xe56aa0 }, { id: 'white', label: 'White', hex: 0xe8e8ea }, { id: 'green', label: 'Green', hex: 0x2aa84a }, { id: 'black', label: 'All black', hex: 0x3a3b40 }],
  chair: [{ id: 'black', label: 'Black', hex: 0x202124 }, { id: 'grey', label: 'Grey', hex: 0x6b6f76 }, { id: 'white', label: 'White', hex: 0xd6d7da }],
};
/** the colour a gadget of yours is (its first if you never picked) */
export function gadgetColour(s: LifeState, id: string): GadgetColour | null {
  const list = GADGET_COLOURS[id];
  if (!list) return null;
  const st = s as LifeState & { hustle?: HustleState };
  return list.find((c) => c.id === st.hustle?.colours?.[id]) ?? list[0];
}
export function setGadgetColour(s: LifeState, id: string, colour: string) { const list = GADGET_COLOURS[id]; if (!list?.some((c) => c.id === colour)) return; (hustleOf(s).colours ??= {})[id] = colour; }
export function hustleOf(s: LifeState): HustleState {
  const st = s as LifeState & { hustle?: HustleState };
  const h = (st.hustle ??= { skill: 0, heat: 0, heatAt: s.minutes, runs: 0, wins: 0, busts: 0, gadgets: [], cashed: 0 });
  // the heat cools: about 4 a day
  const days = (s.minutes - h.heatAt) / 1440;
  if (days > 0) { h.heat = Math.max(0, h.heat - days * 4); h.heatAt = s.minutes; }
  return h;
}
/** a gadget is somewhere: your home country's ones are plain ids ('laptop'), the ones bought abroad 'laptop@france'
 *  (a second setup, like a second car: it stays in that country). hasGadget: one where you are now. */
export const gadgetKey = (s: LifeState, id: string, country = hereCountry(s)) => (country === homeCountry(s) ? id : `${id}@${country}`);
/** the one you bought itself (a gaming chair is not 'chair' here) */
export const ownsGadget = (s: LifeState, id: string) => hustleOf(s).gadgets.includes(gadgetKey(s, id));
/** the better ones count as the plain one: a gaming chair is a chair, a standing desk is a table */
const ALSO: Record<string, string[]> = { chair: ['chair-gaming'], table: ['desk-motor'] };
export const hasGadget = (s: LifeState, id: string) => ownsGadget(s, id) || (ALSO[id] ?? []).some((x) => ownsGadget(s, x));
/** everything you own from Jumbo's gadgets, and the country it's in */
export const gadgetsOwned = (s: LifeState): { id: string; country: string }[] => hustleOf(s).gadgets.map((g) => { const [id, c] = g.split('@'); return { id, country: c ?? homeCountry(s) }; });
const edgeOf = (s: LifeState) => GADGETS.filter((g) => ownsGadget(s, g.id)).reduce((a, g) => a + g.edge, 0);

export function buyGadget(s: LifeState, id: string, colour?: string): { ok: boolean; line: string } {
  const g = GADGETS.find((x) => x.id === id);
  if (!g) return { ok: false, line: '' };
  if (ownsGadget(s, id)) return { ok: false, line: 'You already have one.' };
  if (onItsWay(s, `gadget:${id}`)) return { ok: false, line: 'It’s already on its way: see Your orders.' };
  const addr = deliveryAddress(s);
  if (!addr) return { ok: false, line: 'Jumbo needs an address here: book a hotel room, or get a place, and they’ll bring it there.' };
  if (!spend(s, gadgetPrice(s, g), `Jumbo: ${g.label}`)) return { ok: false, line: 'You can’t afford it.' };
  if (colour) setGadgetColour(s, id, colour);
  const o = placeOrder(s, `gadget:${id}`, g.label)!;
  return { ok: true, line: `${g.label}: ordered. A rider brings it to ${o.to === 'hotel' ? `your hotel (${o.place})` : 'your door'}: about ${Math.round((o.eta - s.minutes) / 5) * 5} minutes. Track it in Your orders.` };
}

/** Time at the laptop: the group chats, the tutorials, the scripts. The skill goes up (slower as it gets high). */
export function practise(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!hasGadget(s, 'laptop') && !hasGadget(s, 'rig')) return { ok: false, line: 'You need a laptop first (Jumbo, on your phone).', events: [] };
  const h = hustleOf(s), comfy = GADGETS.filter((g) => g.comfy && ownsGadget(s, g.id)).length;
  const gain = (6 + comfy * 2) * (1 - h.skill / 120);
  h.skill = Math.min(100, h.skill + gain);
  s.needs.energy = Math.max(0, s.needs.energy - 12);
  s.needs.fun = Math.max(0, s.needs.fun - 4);
  const events = advance(s, 120, { busy: true });
  const line = h.skill < 25 ? 'Two hours in the group chats, watching how the boys talk. Most of it goes over your head.'
    : h.skill < 55 ? 'You’re getting the hang of it: the scripts, the timing, who to message and when.'
    : h.skill < 85 ? 'The others in the chat ask YOU now. You know how it works.' : 'You could do this in your sleep. That’s the problem.';
  return { ok: true, line: `${line} (Skill ${Math.round(h.skill)}/100)`, events };
}

export interface JobOdds { win: number; bust: number }
/** the most a raid freezes (Lagos naira; localPrice turns it into where you are) */
export const FREEZE_MAX = 50_000_000;

/** what a job's chances look like from here (shown before you go) */
export function jobOdds(s: LifeState, size: JobSize): JobOdds {
  const h = hustleOf(s), j = JOBS[size], e = edgeOf(s);
  const win = Math.max(0.05, Math.min(0.9, (0.12 + h.skill * 0.006 + e) * j.win));
  const bust = Math.max(0.01, Math.min(0.6, j.bust + h.heat * 0.005 - e * 0.15 - h.skill * 0.0006));
  return { win, bust };
}

export type JobResult = { kind: 'win'; amount: number; line: string; events: LifeEvent[] } | { kind: 'flop'; line: string; events: LifeEvent[] } | { kind: 'bust'; line: string; events: LifeEvent[]; seized: string[]; frozen: number };

/** Run one. The roll: a cash-out, nothing, or a knock on the door (the game plays the raid; custody is set here). */
export function runJob(s: LifeState, size: JobSize, roll = Math.random()): JobResult | null {
  if (!hasGadget(s, 'laptop') && !hasGadget(s, 'rig')) return null;
  const h = hustleOf(s), j = JOBS[size], o = jobOdds(s, size);
  h.runs++;
  h.heat = Math.min(100, h.heat + j.heat);
  s.needs.energy = Math.max(0, s.needs.energy - 15);
  const events = advance(s, 180, { busy: true });
  if (roll < o.bust) {
    // they came: the gear goes in evidence bags, the account is frozen (most of it), and the cell
    h.busts++;
    // (the setup where you are: the gear here goes in evidence bags; a setup in another country isn't touched)
    const here = (g: string) => (g.split('@')[1] ?? homeCountry(s)) === hereCountry(s), base = (g: string) => g.split('@')[0];
    const seized = h.gadgets.filter((g) => here(g) && base(g) !== 'chair' && base(g) !== 'table');
    h.gadgets = h.gadgets.filter((g) => !seized.includes(g));
    // (most of the cash, up to a ceiling: a raid on a laptop scam doesn't take a fortune made elsewhere; at trillions a
    // share with no cap read as a bug that ate the account)
    const frozen = Math.max(0, Math.min(Math.round(s.money * 0.7), localPrice(s, FREEZE_MAX, 1_000)));
    s.money -= frozen;
    s.ledger.push({ t: s.minutes, amount: -frozen, label: 'Account frozen (evidence)' });
    h.heat = 30;
    const lagos = hereCountry(s) === 'nigeria';
    takeIntoCustody(s, lagos ? 'for internet fraud (EFCC)' : 'for wire fraud', lagos ? 'EFCC Zonal Command, Ikoyi' : hereCountry(s) === 'usa' ? 'the FBI field office' : 'the fraud squad', h.busts > 1);
    nudge(s, 'cautious', 0.1, events);
    s.history.push({ t: s.minutes, text: `Raided: ${lagos ? 'EFCC' : 'the police'} took the laptop and froze the account.` });
    return { kind: 'bust', line: lagos ? '"EFCC! Nobody move!" They\'re at the gate, at the door, everywhere.' : '"Police! Open the door!"', events, seized, frozen };
  }
  if (roll < o.bust + o.win) {
    const r = new Rng((s.seed ^ Math.imul(h.runs + 7, 0x9e3779b1)) >>> 0);
    const amount = localPrice(s, r.range(j.lo, j.hi) * (0.7 + h.skill / 160), 1_000);
    earn(s, amount, 'Credit alert');
    h.wins++; h.cashed += amount;
    s.needs.fun = Math.min(100, s.needs.fun + 15);
    nudge(s, 'honest', -0.04, events);
    s.history.push({ t: s.minutes, text: `Cashed out ${size === 'big' ? 'the big one' : 'a job'}.` });
    return { kind: 'win', amount, line: size === 'big' ? 'Your phone buzzes on the table. And again. The alert, and the zeros keep going.' : 'Credit alert. Your heart is going like a talking drum.', events };
  }
  return { kind: 'flop', line: size === 'small' ? 'Hours of messages and nobody bites. Tomorrow.' : 'They went quiet at the last minute. Nothing. Just the heat.', events };
}

/** how hot it is, in words */
export function heatLine(s: LifeState): string {
  const x = hustleOf(s).heat;
  return x < 15 ? 'Quiet. Nobody’s looking at you.' : x < 40 ? 'Some talk about you in the area.' : x < 70 ? 'People are asking questions. Slow down.' : 'They’re watching you. Any job now is a big risk.';
}
